import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalActions } from '../../../shared/ui/Modal';
import { Button } from '../../../shared/ui/Button';
import { Checkbox } from '../../../shared/ui/Checkbox';
import { Skeleton } from '../../../shared/ui/Skeleton';
import { toast } from '../../../shared/ui/toast';
import { useBulkDeleteMailboxes, useCleanupSuggestions } from '../api';

export function BulkCleanupModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const suggestions = useCleanupSuggestions(open);
  const bulkDelete = useBulkDeleteMailboxes();
  const items = suggestions.data?.items ?? [];
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) setSelected(new Set());
  }, [open]);

  const allChecked = items.length > 0 && selected.size === items.length;
  const someChecked = selected.size > 0;

  const toggle = (id: string, v: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (v) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const toggleAll = (v: boolean) => {
    setSelected(v ? new Set(items.map((m) => m.id)) : new Set());
  };

  const onSubmit = () => {
    bulkDelete.mutate([...selected], {
      onSuccess: () => {
        toast.success(t('mailbox.toasts.deleted'));
        onOpenChange(false);
      },
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('mailbox.bulkCleanup.title')}
      description={t('mailbox.bulkCleanup.subtitle')}
    >
      {suggestions.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm font-medium text-fg">
            <Checkbox
              checked={allChecked ? true : someChecked ? 'indeterminate' : false}
              onCheckedChange={toggleAll}
              aria-label={t('common.selectAll')}
            />
            {t('common.selectAll')}
          </label>
          <div className="max-h-72 space-y-2 overflow-auto">
            {items.map((m) => (
              <label key={m.id} className="flex items-start gap-3">
                <Checkbox
                  checked={selected.has(m.id)}
                  onCheckedChange={(v) => {
                    toggle(m.id, v);
                  }}
                  aria-label={m.address.full}
                />
                <span className="min-w-0">
                  <span className="block text-sm text-fg">{m.address.full}</span>
                  {m.description && <span className="block text-xs text-fg-muted">{m.description}</span>}
                </span>
              </label>
            ))}
            {items.length === 0 && <p className="text-sm text-fg-muted">—</p>}
          </div>
        </div>
      )}

      <ModalActions>
        <Button onClick={onSubmit} disabled={selected.size === 0} loading={bulkDelete.isPending}>
          {t('mailbox.bulkCleanup.submit')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          {t('common.cancel')}
        </Button>
      </ModalActions>
    </Modal>
  );
}
